import { isReactive, isRef, toValue } from 'vue'
import { result as _result, get as _get, isObject } from 'lodash-es'
import DOMPurify from 'dompurify'
import { commons, replaceTitle } from '@/lib/transformer'
import { uploads as lUploads } from '@/lib/label'
import { useConfigsStore } from '@/stores/configs'
import config from '@/config'
import { i18n } from '@/i18n'
import { namedEntityLabel } from '@/lib/dropdownLabel.js'
import { historyFieldLabel, historyFormatBool, historyPhrase } from '@/lib/historyPhrases'

function ticketEnumValue(group, change, prop) {
  const v = change[prop]
  if (v == null || v === '') return v
  const key = `tickets.${group}.${v}`
  return i18n.global.te(key) ? i18n.global.t(key) : v
}

/** Подпись активности: строка из audit trail или {name} из полной related-модели. */
function formatActivityTypeLabel(act) {
  if (act == null || act === '') return null
  if (typeof act === 'object') {
    const name = namedEntityLabel(act) || act.value
    if (name == null || name === '') return null
    return String(name)
  }
  return String(act)
}

/** Язык подписи ТН/ЛП в истории — по текущей раскладке RTMS. */
function isEnglishHistoryLocale() {
  return String(toValue(i18n.global.locale) || '').toLowerCase().startsWith('en')
}

/**
 * Скалярное имя из related/поля: объекты (вложенный ТН и т.п.) не превращать в «[object Object]».
 *
 * @param {unknown} value
 * @returns {string}
 */
function historyScalarName(value) {
  if (value == null || value === '') return ''
  if (typeof value === 'string' || typeof value === 'number') {
    const s = String(value).trim()
    return s && s !== '[object Object]' ? s : ''
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    return (
      historyScalarName(value.name)
      || historyScalarName(value.name_eng)
      || historyScalarName(value.value)
      || historyScalarName(value.caption)
    )
  }
  return ''
}

/**
 * Имя ТН/ЛП из related: catalogs history() отдаёт {name, name_eng},
 * audit trail / старые записи — строку. Без unwrap получается «[object Object]».
 */
function catalogHistoryName(rel) {
  if (rel == null || rel === '') return ''
  if (typeof rel === 'string' || typeof rel === 'number') {
    return historyScalarName(rel)
  }
  if (typeof rel === 'object' && !Array.isArray(rel)) {
    const preferEn = isEnglishHistoryLocale()
    const en = historyScalarName(rel.name_eng)
    const ru = historyScalarName(rel.name)
    const fromTm = catalogHistoryName(rel.medicine_trademark)
    const fromValue = historyScalarName(rel.value)
    if (preferEn) {
      return en || ru || fromTm || fromValue
    }
    return ru || en || fromTm || fromValue
  }
  return ''
}

/** Базовый URL LTMS для ссылок в истории RTMS (например, подача SoH при загрузке АПП). */
export function ltmsPublicBaseUrl() {
  const explicit = (config.VITE_LTMS_URL || '').replace(/\/$/, '')
  if (explicit) {
    return explicit
  }
  const rtms = (config.VITE_RTMS_URL || '').replace(/\/$/, '')
  if (rtms.includes('://rtms.')) {
    return rtms.replace('://rtms.', '://ltms.')
  }
  return 'http://ltms.local'
}

/** Строка заголовка пользователя в истории (в т.ч. JSON `{username,fio}` из SSO). */
function formatUserRelationLabel(rel) {
  if (rel == null || rel === '') return null
  let v = rel
  if (typeof v === 'string') {
    const t = v.trim()
    if (t.startsWith('{') && t.endsWith('}')) {
      try {
        const p = JSON.parse(t)
        if (p && typeof p === 'object' && !Array.isArray(p)) v = p
      } catch {
        /* оставляем строку */
      }
    }
  }
  if (isObject(v) && !Array.isArray(v)) {
    const username = (v.username ?? '').toString().trim()
    const fio = (v.fio ?? '').toString().trim()
    if (username || fio) return [username, fio].filter(Boolean).join(', ')
    return null
  }
  if (typeof v === 'string' && v.trim()) return v.trim()
  return null
}

////---

/**
 * @callback ChangeFormatter
 * @param {Object} change certain history entry
 * @param {Object} relations all given relations as {RelName:{RelId: Rel}}
 */

/**
 * @callback ChangeValueGetter
 * @param {Object} changeset full changeset (batch)
 * @param {Object} change certain history entry
 * @param {Object|String} relation relation name
 * @param {String} prop what prop in change entry value|old_value
 */

////---

////---

function historyPart(key, params) {
  return historyPhrase(`parts.${key}`, params ?? {})
}

/**
 * Order number in expense history entity title.
 * @param {boolean} asLink - false in the order's own history (link would point to the same page)
 */
function expenseOrderLink(rel, asLink = true) {
  const orderId = rel?.order?.order_id
  if (orderId == null || orderId === '') return null
  const label = historyPhrase('models.order', { link: `№${orderId}` })
  return asLink ? `<a href="/orders/${orderId}">${label}</a>` : label
}

function joinEntityParts(parts) {
  return parts.filter(Boolean).join(', ')
}

function formatRelStockParts(stock, { serialAsDevice = false } = {}) {
  const parts = []
  if (stock?.serie) {
    parts.push(historyPart(serialAsDevice ? 'serialNumber' : 'batchNumber', { value: stock.serie }))
  }
  const actLabel = formatActivityTypeLabel(stock?.activity_type)
  if (actLabel) {
    parts.push(historyPart('activity', { name: actLabel }))
  }
  const trademarkName = namedEntityLabel(stock?.medicine_trademark)
  if (trademarkName) {
    parts.push(historyPart('trademark', { name: trademarkName }))
  }
  return parts
}

function formatStockRelInfo(rel) {
  const parts = []
  const catalogItem = rel.consumable || rel.material_nd || rel.material_comm || rel.device || rel.medicine
  const catalogLabel = namedEntityLabel(catalogItem)
  if (catalogLabel) parts.push(catalogLabel)
  else if (rel.catalog_name) parts.push(rel.catalog_name)
  const actLabel = formatActivityTypeLabel(rel.activity_type)
  if (actLabel) parts.push(historyPart('activity', { name: actLabel }))
  if (rel.serie) parts.push(historyPart('serie', { value: rel.serie }))
  if (rel.valid_till) parts.push(historyPart('validTill', { date: commons.dateLocal(rel.valid_till) }))
  const trademarkLabel = namedEntityLabel(rel.medicine_trademark)
  if (trademarkLabel) parts.push(historyPart('trademark', { name: trademarkLabel }))
  else if (rel.trademark_name) parts.push(historyPart('trademark', { name: rel.trademark_name }))
  if (rel.medicinal_form) parts.push(historyPart('dosageForm', { value: rel.medicinal_form }))
  return parts.length ? ` (${parts.join(', ')})` : ''
}

function formatDestructionStockParts(stock, catalog, { medicine = false } = {}) {
  const parts = []
  if (medicine) {
    const med = stock.medicine
    const trademark = med?.medicine_trademark
    const name = namedEntityLabel(trademark) || namedEntityLabel(med) || med?.name_short
    if (name) parts.push(name)
    if (med?.manufacturer) parts.push(med.manufacturer)
    if (med?.code) parts.push(historyPart('catalogNoShort', { code: med.code }))
  } else {
    const name = namedEntityLabel(catalog)
    if (name) parts.push(name)
  }
  if (stock.serie) parts.push(historyPart('serie', { value: stock.serie }))
  if (stock.valid_till) parts.push(historyPart('validTill', { date: commons.dateLocal(stock.valid_till) }))
  const actLabel = formatActivityTypeLabel(stock.activity_type)
  if (actLabel) parts.push(actLabel)
  if (!medicine) {
    const trademark = namedEntityLabel(stock?.medicine_trademark)
    if (trademark) parts.push(historyPart('trademarkShort', { name: trademark }))
  }
  return parts.length ? ` (${parts.join(', ')})` : ''
}

function formatExpenseEntity(ch, rel, { namedKey, idKey, catalogKey, serialAsDevice = false, asLink = true }) {
  if (!rel) {
    return historyPhrase(`models.${idKey}`, { id: ch.model_id })
  }
  const parts = []
  const catalog = rel[catalogKey]
  const catalogLabel = namedEntityLabel(catalog)
  if (catalogLabel) {
    parts.push(historyPhrase(`models.${namedKey}`, { name: catalogLabel }))
  } else {
    parts.push(historyPhrase(`models.${idKey}`, { id: ch.model_id }))
  }
  if (rel.stock) {
    parts.push(...formatRelStockParts(rel.stock, { serialAsDevice }))
  }
  const orderLink = expenseOrderLink(rel, asLink)
  if (orderLink) parts.push(orderLink)
  return joinEntityParts(parts)
}

function formatPurchaseEntity(ch, rel, { linkKey, fallbackKey, hrefType }) {
  if (isObject(rel) && rel.id) {
    const link = `<a href="/purchases/${rel.id}?purchase_type=${hrefType}">${rel.id}</a>`
    return historyPhrase(`models.${linkKey}`, { link })
  }
  return historyPhrase(`models.${fallbackKey}`, { id: ch.model_id })
}

function formatStockEntity(ch, rel, { linkKey, fallbackKey }) {
  if (isObject(rel)) {
    return historyPhrase(`models.${linkKey}`, {
      link: genLink(ch, rel.id, true),
      info: formatStockRelInfo(rel),
    })
  }
  return historyPhrase(`models.${fallbackKey}`, { id: ch.model_id })
}

function formatDestructionEntity(ch, rel, { arrayKey, catalogKey, stockKey, fallbackIdKey, medicine = false }) {
  if (isObject(rel) && Array.isArray(rel[arrayKey])) {
    const destruction = rel[arrayKey].find(d => d.id === ch.model_id)
    let reqNum = ''
    if (destruction.destruction_id) {
      reqNum = `<a href="/destructions/${destruction.destruction_id}">${destruction.destruction_id}</a>`
    }
    const stock = destruction.stock
    const stockNum = genLinkUnic(ch, stock.id, true)
    const catalog = catalogKey ? stock[catalogKey] : undefined
    const posInfo = formatDestructionStockParts(stock, catalog, { medicine })
    return historyPhrase('models.destructionStockRequest', {
      request: reqNum ? `${reqNum} ` : '',
      stock: stockNum,
      info: posInfo,
    })
  }
  if (isObject(rel) && rel.stock_id) {
    return historyPhrase(`models.${stockKey}`, { stock: genLinkUnic(ch, rel.stock_id, true) })
  }
  return historyPhrase(`models.${fallbackIdKey}`, { id: ch.model_id })
}

function pushExpenseSubjectDetails(parts, subject, { catalogKey, serialAsDevice = false, trademarkPath = false }) {
  let catalog
  if (trademarkPath) {
    catalog = subject.medicine?.medicine_trademark || {}
  } else {
    catalog = subject[catalogKey] || {}
  }
  const catalogLabel = namedEntityLabel(catalog)
  if (catalogLabel) parts.push(historyPart('name', { name: catalogLabel }))
  if (catalog.manufacturer) parts.push(historyPart('manufacturer', { name: catalog.manufacturer }))
  if (catalog.code) parts.push(historyPart('catalogNumber', { code: catalog.code }))
  if (subject.stock?.serie) {
    parts.push(
      historyPart(serialAsDevice ? 'serialNumber' : 'batchNumber', { value: subject.stock.serie })
    )
  }
  if (subject.stock?.valid_till) {
    parts.push(historyPart('validTillColon', { date: commons.dateLocal(subject.stock.valid_till) }))
  }
}

const COMMENT_PARENT_LABELS = {
  Receptions: 'comments.parentReceptions',
  Orders: 'comments.parentOrders',
  Destructions: 'comments.parentDestructions',
  WriteOffs: 'comments.parentWriteOffs',
  Ticket: 'comments.parentTicket',
}


/** Сырая ссылка entity.field из записи истории дропдауна (Dropdowns / DropdownsCatalog). */
function dropdownHistoryRefRaw(ch) {
  const changes = ch?.changes || []
  const refFromBackend = changes.map(c => c?.dropdown_ref).find(Boolean)
  const c0 = changes[0] || {}
  let field = refFromBackend || c0.field || ''
  if (!field) {
    let iv = c0.initial_value
    if (typeof iv === 'string' && iv.trim().startsWith('{')) {
      try {
        iv = JSON.parse(iv)
      } catch {
        iv = null
      }
    }
    if (iv && typeof iv === 'object' && iv.entity_name && iv.field) {
      field = `${iv.entity_name}.${iv.field}`
    }
  }
  if (
    !field &&
    Array.isArray(c0.path) &&
    c0.path.length &&
    !/^(key|value)$/i.test(c0.path.join('.'))
  ) {
    field = c0.path.join('.')
  }
  return field ? String(field) : ''
}

/** Подпись объекта для Dropdowns / DropdownsCatalog в Audit Trail. */
function dropdownCatalogEntityName(ch, rel, il, prefix) {
  const changes = ch?.changes || []
  const c0 = changes[0] || {}
  const field = dropdownHistoryRefRaw(ch)
  const translated = field
    .split('.')
    .filter(Boolean)
    .map(part => replaceTitle(part))
    .join(' / ')
  const ivRaw = c0.initial_value
  let iv = ivRaw && typeof ivRaw === 'object' ? ivRaw : null
  if (!iv && typeof ivRaw === 'string' && ivRaw.trim().startsWith('{')) {
    try {
      iv = JSON.parse(ivRaw)
    } catch {
      iv = null
    }
  }
  const ddVal =
    changes.map(c => c?.dd_value).find(v => v != null && v !== '') ??
    c0.dd_value ??
    iv?.value
  const ddKey =
    changes.map(c => c?.dd_key).find(v => v != null && v !== '') ?? c0.dd_key ?? iv?.key
  // Колонка «Объект»: человекочитаемое значение дропдауна; если нет — ключ (старые записи)
  const valueShown =
    ddVal != null && ddVal !== ''
      ? String(ddVal)
      : rel != null && rel !== ''
        ? String(rel)
        : ddKey != null && ddKey !== ''
          ? String(ddKey)
          : ''
  const bits = [prefix]
  if (translated) bits.push(translated)
  // AS-1477: значение — ссылкой в раздел «Выпадающие списки» (как в QEMS)
  if (valueShown) bits.push(il ? dropdownSectionLink(ch, valueShown) : valueShown)
  return bits.join(' ')
}

/** Ссылка на раздел «Выпадающие списки» с раскрытием нужной секции (AS-1477, как в QEMS). */
function dropdownSectionLink(ch, txt) {
  const raw = dropdownHistoryRefRaw(ch)
  const parts = raw.split('.').filter(Boolean)
  if (parts.length < 2) return txt
  const q = `?dd_entity=${encodeURIComponent(parts[0])}&dd_field=${encodeURIComponent(parts.slice(1).join('.'))}`
  return `<a href="/dropdowns${q}">${txt}</a>`
}

export const models = {
  Activities: {
    entityName: (ch, rel, il) => {
      if (isObject(rel)) {
        return historyPhrase('models.activity', { link: genLinkUnic(ch, rel.activity_id, true) })
      }
      return historyPhrase('models.activity', { link: `«${ch.model_id}»` })
    },
    prefix: 'activities',
  },
  ActivityTypes: {
    entityName: (ch, _, il) =>
      historyPhrase('models.activityType', { id: genLink(ch, ch.model_id, il) }),
    prefix: 'catalog/activity_types',
  },
  Consumables: {
    entityName: (ch, rel, il) =>
      historyPhrase('models.consumable', { name: genLink(ch, rel, il) }),
    prefix: 'catalog/consumables',
  },
  Comments: {
    entityName: (ch, rel, il) => {
      const histKey = ch.hist_key || ''
      const parts = histKey.split(':')
      if (parts.length >= 2) {
        const parentModel = parts[0]
        const parentId = parts[1] !== 'None' ? parts[1] : ''
        const parentConfig = models[parentModel]
        const labelKey = COMMENT_PARENT_LABELS[parentModel] || 'comments.parentFallback'
        const label = historyPhrase(labelKey)
        if (parentConfig?.prefix && parentId && il) {
          const link = `<a href="/${parentConfig.prefix}/${parentId}">${parentId}</a>`
          return historyPhrase('comments.withId', { label, id: link })
        }
        return parentId
          ? historyPhrase('comments.withId', { label, id: parentId })
          : historyPhrase('models.comment')
      }
      return historyPhrase('models.comment')
    },
  },
  TicketComment: {
    entityName: (ch, rel, il) => {
      const histKey = ch.hist_key || ''
      const parts = histKey.split(':')
      if (parts.length >= 2 && parts[0] === 'Ticket') {
        const parentId = parts[1] !== 'None' ? parts[1] : ''
        if (parentId && il) {
          return historyPhrase('models.ticketComment', {
            link: `<a href="/tickets/${parentId}">#${parentId}</a>`,
          })
        }
        return historyPhrase('models.ticketComment', {
          link: `#${parentId || ch.model_id}`,
        })
      }
      return historyPhrase('models.ticketCommentFallback', { id: ch.model_id })
    },
  },
  Ticket: {
    entityName: (ch, rel, il) => {
      const id = rel?.id ?? ch.model_id
      const link = il ? `<a href="/tickets/${id}">#${id}</a>` : `#${id}`
      return historyPhrase('models.ticket', { link })
    },
    prefix: 'tickets',
  },
  ConsumablesExpenses: {
    entityName: (ch, rel, asLink = true) =>
      formatExpenseEntity(ch, rel, {
        namedKey: 'consumablesExpenseNamed',
        idKey: 'consumablesExpenseId',
        catalogKey: 'consumable',
        asLink,
      }),
  },
  ConsumablesPurchases: {
    entityName: (ch, rel) =>
      formatPurchaseEntity(ch, rel, {
        linkKey: 'consumablesPurchase',
        fallbackKey: 'consumablesPurchaseFallback',
        hrefType: 'consumable',
      }),
    prefix: 'purchases/consumables',
  },
  ConsumablesStock: {
    entityName: (ch, rel) =>
      formatStockEntity(ch, rel, {
        linkKey: 'consumablesStock',
        fallbackKey: 'consumablesStockFallback',
      }),
    prefix: 'stock/consumables',
  },
  Devices: {
    entityName: (ch, rel, il) => historyPhrase('models.device', { name: genLink(ch, rel, il) }),
    prefix: 'catalog/devices',
  },
  DevicesExpenses: {
    entityName: (ch, rel, asLink = true) =>
      formatExpenseEntity(ch, rel, {
        namedKey: 'devicesExpenseNamed',
        idKey: 'devicesExpenseId',
        catalogKey: 'device',
        serialAsDevice: true,
        asLink,
      }),
  },
  DevicesPurchases: {
    entityName: (ch, rel) =>
      formatPurchaseEntity(ch, rel, {
        linkKey: 'devicesPurchase',
        fallbackKey: 'devicesPurchaseFallback',
        hrefType: 'device',
      }),
    prefix: 'purchases',
  },
  DevicesStock: {
    entityName: (ch, rel) =>
      formatStockEntity(ch, rel, {
        linkKey: 'devicesStock',
        fallbackKey: 'devicesStockFallback',
      }),
    prefix: 'stock/devices',
  },
  Laboratories: {
    entityName: (ch, _, il) =>
      historyPhrase('models.laboratory', { id: genLink(ch, ch.model_id, il) }),
    prefix: 'catalog/laboratories',
  },
  Receptions: {
    entityName: (ch, rel, il) => {
      if (isObject(rel)) {
        return historyPhrase('models.reception', { link: genLink(ch, ch.model_id, true) })
      }
      return historyPhrase('models.receptionFallback', { id: ch.model_id })
    },
    prefix: 'receptions',
  },
  WriteOffs: {
    entityName: (ch, rel, il) => {
      if (isObject(rel) && Array.isArray(rel.WriteOffs)) {
        const writeoff = rel.WriteOffs.find(d => d.id === ch.model_id)
        const num = writeoff?.writeoff_id || ch.model_id
        const actLink = label => {
          if (!il) return label
          const path = writeoff?.commissioning ? 'documents/commissioning' : 'documents/debiting'
          return `<a href="/${path}/${ch.model_id}">${label}</a>`
        }
        if (writeoff?.destruction) {
          const destructionsKey = Object.keys(rel).find(k => k.includes('Destructions'))
          const destructionsArr = destructionsKey ? rel[destructionsKey] : null
          const destruction = Array.isArray(destructionsArr)
            ? (destructionsArr.find(d => d.id === writeoff.destruction_id) ?? destructionsArr[0])
            : null
          const stock = destruction?.stock ?? writeoff.stock
          if (!stock) {
            return historyPhrase('models.writeOffDestruction', {
              link: actLink(num),
            })
          }
          const stockNum = genLinkUnic(ch, stock.id, true)
          const destructionsCatalogProp = {
            ConsumablesDestructions: 'consumable',
            DevicesDestructions: 'device',
            MaterialsCommDestructions: 'material_comm',
            MaterialsNdDestructions: 'material_nd',
            MedicinesDestructions: 'medicine',
          }
          const catalog =
            destructionsKey && destructionsCatalogProp[destructionsKey]
              ? stock[destructionsCatalogProp[destructionsKey]]
              : undefined
          const posInfo = formatDestructionStockParts(stock, catalog, {
            medicine: destructionsKey === 'MedicinesDestructions',
          })
          return historyPhrase('models.writeOffDestructionStock', {
            link: actLink(num),
            stock: stockNum,
            info: posInfo,
          })
        }
        return historyPhrase('models.writeOffFallback', { id: num })
      }
      return historyPhrase('models.writeOffFallback', { id: ch.model_id })
    },
    prefix: 'documents/debiting',
    historyPrefix: 'writeoffs',
    fields: {
      order: 'Заявка',
      destruction: 'Запрос на уничтожение',
      date_from: 'Начало периода',
      date_to: 'Конец периода',
      writeoff_id: '№ акта',
      current_version: 'Версия',
      comment_approve: 'Комментарий при возврате',
      date_aligned: 'Дата согласования',
    },
  },
  MaterialsComm: {
    entityName: (ch, rel, il) =>
      historyPhrase('models.materialsComm', { name: genLink(ch, rel, il) }),
    prefix: 'catalog/materials_comm',
  },
  MaterialsCommExpenses: {
    entityName: (ch, rel, asLink = true) =>
      formatExpenseEntity(ch, rel, {
        namedKey: 'materialsCommExpenseNamed',
        idKey: 'materialsCommExpenseId',
        catalogKey: 'material_comm',
        asLink,
      }),
  },
  MaterialsCommPurchases: {
    entityName: (ch, rel) =>
      formatPurchaseEntity(ch, rel, {
        linkKey: 'materialsCommPurchase',
        fallbackKey: 'materialsCommPurchaseFallback',
        hrefType: 'material_comm',
      }),
    prefix: 'purchases/materials_comm',
  },
  MaterialsCommStock: {
    entityName: (ch, rel) =>
      formatStockEntity(ch, rel, {
        linkKey: 'materialsCommStock',
        fallbackKey: 'materialsCommStockFallback',
      }),
    prefix: 'stock/materials_comm',
  },
  MaterialsNd: {
    entityName: (ch, rel, il) =>
      historyPhrase('models.materialsNd', { name: genLink(ch, rel, il) }),
    prefix: 'catalog/materials_nd',
  },
  MaterialsNdExpenses: {
    entityName: (ch, rel, asLink = true) =>
      formatExpenseEntity(ch, rel, {
        namedKey: 'materialsNdExpenseNamed',
        idKey: 'materialsNdExpenseId',
        catalogKey: 'material_nd',
        asLink,
      }),
  },
  MaterialsNdPurchases: {
    entityName: (ch, rel) =>
      formatPurchaseEntity(ch, rel, {
        linkKey: 'materialsNdPurchase',
        fallbackKey: 'materialsNdPurchaseFallback',
        hrefType: 'material_nd',
      }),
    prefix: 'purchases/materials_nd',
  },
  MaterialsNdStock: {
    entityName: (ch, rel) =>
      formatStockEntity(ch, rel, {
        linkKey: 'materialsNdStock',
        fallbackKey: 'materialsNdStockFallback',
      }),
    prefix: 'stock/materials_nd',
  },
  Medicines: {
    entityName: (ch, rel, il) =>
      historyPhrase('models.medicine', {
        name: genLink(ch, catalogHistoryName(rel) || ch.model_id, il),
      }),
    prefix: 'catalog/medicines',
  },
  MedicinesExpenses: {
    entityName: (ch, rel, asLink = true) => {
      if (!rel) {
        return historyPhrase('models.medicineExpenseId', { id: ch.model_id })
      }
      // full_history: medicine.medicine_trademark; history(): плоский {name, medicinal_form, serie, ...}
      const trademark = rel.medicine?.medicine_trademark
      const name = namedEntityLabel(trademark) || rel.name
      const parts = []
      if (name) {
        parts.push(historyPhrase('models.medicineExpenseNamed', { name }))
      } else {
        parts.push(historyPhrase('models.medicineExpenseId', { id: ch.model_id }))
      }
      const medicinalForm = trademark?.medicinal_form?.value || rel.medicinal_form
      if (medicinalForm) {
        parts.push(medicinalForm)
      }
      if (trademark?.manufacturer) {
        parts.push(historyPart('manufacturer', { name: trademark.manufacturer }))
      }
      if (trademark?.code) {
        parts.push(historyPart('catalogNumber', { code: trademark.code }))
      }
      if (rel.stock) {
        parts.push(...formatRelStockParts(rel.stock))
      } else {
        if (rel.serie) {
          parts.push(historyPart('batchNumber', { value: rel.serie }))
        }
        const actLabel = formatActivityTypeLabel(rel.activity_type)
        if (actLabel) parts.push(historyPart('activity', { name: actLabel }))
      }
      const orderLink = expenseOrderLink(rel, asLink)
      if (orderLink) parts.push(orderLink)
      return joinEntityParts(parts)
    },
  },
  MedicinesPurchases: {
    entityName: (ch, rel) =>
      formatPurchaseEntity(ch, rel, {
        linkKey: 'medicinePurchase',
        fallbackKey: 'medicinePurchaseFallback',
        hrefType: 'medicine',
      }),
    prefix: 'purchases/medicines',
  },
  MedicinesStock: {
    entityName: (ch, rel) =>
      formatStockEntity(ch, rel, {
        linkKey: 'medicinesStock',
        fallbackKey: 'medicinesStockFallback',
      }),
    prefix: 'stock/medicines',
  },
  MedicineTrademarks: {
    entityName: (ch, rel, il) =>
      historyPhrase('models.medicineTrademark', {
        name: genLink(ch, catalogHistoryName(rel) || ch.model_id, il),
      }),
    prefix: 'catalog/medicine_trademarks',
  },
  Orders: {
    entityName: (ch, rel, il) => {
      if (isObject(rel)) {
        const orderId = rel.order_id ?? ch.model_id
        return historyPhrase('models.order', { link: genLinkUnic(ch, orderId, true) })
      }
      return historyPhrase('models.orderFallback', { id: ch.model_id })
    },
    prefix: 'orders',
  },
  Templates: {
    entityName: (ch, rel) => historyPhrase('models.template', { id: ch.model_id }),
    prefix: 'templates',
  },
  Vendors: {
    entityName: (ch, rel, il) => historyPhrase('models.vendor', { name: genLink(ch, rel, il) }),
    prefix: 'catalog/vendors',
  },
  Uploads: {
    entityName: ch => {
      let l = [historyPhrase('models.document')]
      const change0 = ch.changes?.[0] || {}
      const div = change0.division
      const fname = change0.filename
      if (div) {
        l.push(`«${lUploads[div] || div}»`)
      }
      if (fname) {
        l.push(fname)
      }
      const submissionId =
        change0.ltms_submission_id ??
        change0.metadata?.ltms_submission_id ??
        null
      if (submissionId != null && submissionId !== '') {
        const base = ltmsPublicBaseUrl()
        l.push(
          `на основании подачи №<a href="${base}/submissions/${submissionId}" target="_blank" rel="noopener noreferrer">${submissionId}</a>`
        )
      }
      return l.join(' ')
    },
  },
  User: {
    entityName: (ch, rel, il) => {
      const label = formatUserRelationLabel(rel) ?? ch.model_id
      return historyPhrase('models.user', { link: genLink(ch, label, il) })
    },
    prefix: 'users',
  },
  AssignedUsers: {
    entityName: (ch, rel, il) => {
      if (!isObject(rel)) {
        return historyPhrase('models.assignedUserFallback', { id: ch.model_id })
      }
      const userName =
        formatUserRelationLabel(rel?.assigned_user) ||
        rel?.assigned_user?.fio ||
        rel?.assigned_user?.username
      const roleName = rel?.assigned_role?.name
      const label = roleName
        ? historyPhrase('models.assignedUserWithRole', { user: userName, role: roleName })
        : userName
      return label
        ? historyPhrase('models.assignedUser', {
            link: genLink(ch, label, il, rel?.assigned_user?.id),
          })
        : historyPhrase('models.assignedUserFallback', { id: ch.model_id })
    },
    fields: { assigned_user: fmtAssignedUserWithRole },
    prefix: 'users',
  },
  Dropdowns: {
    entityName: (ch, rel, il) =>
      dropdownCatalogEntityName(ch, rel, il, historyPhrase('models.dropdown')),
  },
  DropdownsCatalog: {
    entityName: (ch, rel, il) =>
      dropdownCatalogEntityName(ch, rel, il, historyPhrase('models.dropdownCatalog')),
  },
  Destructions: {
    entityName: (ch, rel, il) => {
      if (isObject(rel)) {
        return historyPhrase('models.destructionRequest', { link: genLink(ch, ch.model_id, true) })
      }
      return historyPhrase('models.destructionRequestFallback', { id: ch.model_id })
    },
    prefix: 'destructions',
  },
  ConsumablesDestructions: {
    entityName: (ch, rel, il) =>
      formatDestructionEntity(ch, rel, {
        arrayKey: 'ConsumablesDestructions',
        catalogKey: 'consumable',
        stockKey: 'consumablesDestructionStock',
        fallbackIdKey: 'consumablesDestructionFallback',
      }),
    prefix: 'stock/consumables',
  },
  DevicesDestructions: {
    entityName: (ch, rel, il) =>
      formatDestructionEntity(ch, rel, {
        arrayKey: 'DevicesDestructions',
        catalogKey: 'device',
        stockKey: 'devicesDestructionStock',
        fallbackIdKey: 'devicesDestructionFallback',
      }),
    prefix: 'stock/devices',
  },
  MaterialsCommDestructions: {
    entityName: (ch, rel, il) =>
      formatDestructionEntity(ch, rel, {
        arrayKey: 'MaterialsCommDestructions',
        catalogKey: 'material_comm',
        stockKey: 'materialsCommDestructionStock',
        fallbackIdKey: 'materialsCommDestructionFallback',
      }),
    prefix: 'stock/materials_comm',
  },
  MaterialsNdDestructions: {
    entityName: (ch, rel, il) =>
      formatDestructionEntity(ch, rel, {
        arrayKey: 'MaterialsNdDestructions',
        catalogKey: 'material_nd',
        stockKey: 'materialsNdDestructionStock',
        fallbackIdKey: 'materialsNdDestructionFallback',
      }),
    prefix: 'stock/materials_nd',
  },
  MedicinesDestructions: {
    entityName: (ch, rel, il) =>
      formatDestructionEntity(ch, rel, {
        arrayKey: 'MedicinesDestructions',
        catalogKey: null,
        stockKey: 'medicineDestructionStock',
        fallbackIdKey: 'medicineDestructionFallback',
        medicine: true,
      }),
    prefix: 'stock/medicines',
  },
  Manufacturers: {
    entityName: (ch, rel, il) =>
      historyPhrase('models.manufacturerCatalog', {
        name: genLink(
          ch,
          rel != null && String(rel).trim() !== '' ? rel : ch.model_id,
          il
        ),
      }),
    prefix: 'catalog/manufacturers',
  },
  MedicineManufacturer: {
    entityName: (ch, rel) => {
      let manufacturerName = null
      if (rel && rel['Manufacturers']) {
        const manufacturer = rel['Manufacturers'][ch.model_id]
        if (manufacturer) {
          manufacturerName = manufacturer.name || manufacturer
          if (manufacturer.address) {
            manufacturerName += `, (${manufacturer.address})`
          }
        }
      }
      if (!manufacturerName && rel && rel['MedicineManufacturer']) {
        for (const medicineId in rel['MedicineManufacturer']) {
          const manufacturers = rel['MedicineManufacturer'][medicineId]
          if (Array.isArray(manufacturers)) {
            const manufacturer = manufacturers.find(m => m.id === ch.model_id)
            if (manufacturer) {
              if (manufacturer.name) {
                manufacturerName = manufacturer.name
              }
              if (manufacturer.address) {
                manufacturerName += `, (${manufacturer.address})`
              }
              break
            }
          }
        }
      }
      return manufacturerName
        ? historyPhrase('models.manufacturerNamed', { name: manufacturerName })
        : historyPhrase('models.manufacturerId', { id: ch.model_id })
    },
  },
  ActionRequests: {
    entityName: (ch, rel, il) => {
      const acction_requests = rel?.ActionRequests
      if (!Array.isArray(acction_requests)) return undefined
      const acction_request = rel?.ActionRequests.find(d => d.id === ch.model_id)
      const action =
        (acction_request && acction_request.action) ||
        ch.changes?.[0]?.value ||
        ch.changes?.[0]?.old_value ||
        ''
      const knownActions = ['delete', 'approve', 'edit', 'reserve']
      const actionLabel = knownActions.includes(action)
        ? historyPhrase(`actionRequest.actions.${action}`)
        : action || historyPhrase('actionRequest.actions.fallback')

      const subjectName = acction_request?.subject_name || ch.model_name
      const subjectId = acction_request?.subject_id || ch.model_id
      const subjectLabelKey = `actionRequest.subjects.${subjectName}`
      const subjectLabel = historyPhrase(
        [
          'Activities',
          'Orders',
          'MedicinesExpenses',
          'MaterialsNdExpenses',
          'MaterialsCommExpenses',
          'DevicesExpenses',
          'ConsumablesExpenses',
        ].includes(subjectName)
          ? subjectLabelKey
          : 'actionRequest.subjects.fallback'
      )

      let subjectDetails = ''
      if (subjectName && subjectId && rel) {
        const subjectRelations = rel[subjectName]
        let subject = null
        if (Array.isArray(subjectRelations)) {
          subject = subjectRelations.find(d => d.id === subjectId)
        } else if (subjectRelations && typeof subjectRelations === 'object') {
          subject = subjectRelations[subjectId] || subjectRelations
        }
        if (subject) {
          const parts = []
          if (subjectName === 'MaterialsNdExpenses' && subject.material_nd) {
            pushExpenseSubjectDetails(parts, subject, { catalogKey: 'material_nd' })
          }
          if (subjectName === 'MedicinesExpenses' && subject.medicine) {
            pushExpenseSubjectDetails(parts, subject, { trademarkPath: true })
          }
          if (subjectName === 'MaterialsCommExpenses' && subject.material_comm) {
            pushExpenseSubjectDetails(parts, subject, { catalogKey: 'material_comm' })
          }
          if (subjectName === 'DevicesExpenses' && subject.device) {
            pushExpenseSubjectDetails(parts, subject, { catalogKey: 'device', serialAsDevice: true })
          }
          if (subjectName === 'ConsumablesExpenses' && subject.consumable) {
            pushExpenseSubjectDetails(parts, subject, { catalogKey: 'consumable' })
          }
          if (parts.length) {
            subjectDetails = ` (${parts.join(', ')})`
          }
        }
      }

      const requesterName = acction_request?.requester?.fio || acction_request?.requester?.username
      const approverName = acction_request?.approver?.fio || acction_request?.approver?.username
      let fromTo = ''
      if (requesterName && approverName) {
        fromTo = historyPhrase('parts.fromRequesterToApprover', { requester: requesterName, approver: approverName })
      } else if (requesterName) {
        fromTo = historyPhrase('parts.fromRequester', { requester: requesterName })
      }

      const commentRequest = rel?.comment_request?.trim()
      const commentPart = commentRequest
        ? historyPhrase('parts.commentSeparator', { comment: commentRequest })
        : ''

      return historyPhrase('actionRequest.summary', {
        action: actionLabel,
        subject: subjectLabel,
        details: subjectDetails,
        fromTo,
        comment: commentPart,
      })
    },
  },
}

function genLink(ch, txt, il, linkId) {
  const model = models[ch.model_name]
  const id = linkId !== undefined ? linkId : ch.model_id
  // related ТН/ЛП — объект {name,name_eng}; не подставлять его в <a> как [object Object]
  const label = catalogHistoryName(txt) || historyScalarName(txt) || String(id ?? '')
  const asLink = il === true
  return asLink
      ? model
          ? typeof model.reflink === 'function'
              ? model.reflink(ch, label)
              : `<a href="/${model.prefix}/${id}">${label}</a>`
          : label
      : label
}
function genLinkUnic(ch, txt, il) {
  const model = models[ch.model_name]
  return il
      ? model
          ? typeof model.reflink === 'function'
              ? model.reflink(ch, txt)
              : `<a href="/${model.prefix}/${txt}">${txt}</a>`
          : txt
      : txt
}

const FIELD_COMMON_KEYS = [
  'activity', 'activity_subtype', 'activity_type', 'address', 'assigned_at', 'assigned_role',
  'date_created', 'date_from', 'date_to', 'date_aligned', 'current_version', 'destruction', 'email',
  'order_id', 'writeoff_id', 'airbill', 'airbill_date', 'aligned_by', 'aligned',
  'amount', 'archived_at', 'assign_role', 'assign_user', 'responsible_manager', 'bill_date', 'bill', 'bill_num', 'medicine',
  'vortex_invoice', 'code', 'comment', 'consignment_date', 'consignment_note', 'consignment', 'consumable',
  'contact', 'device', 'dispatch_date_expected', 'dosage', 'export', 'gtd_date', 'gtd_serial', 'gtd',
  'invoice', 'invoice_date', 'invoice_serial', 'import_permit', 'import_permit_date', 'laboratory',
  'manufacturer', 'material_comm', 'material_nd', 'medicine_trademark', 'site_number', 'mhh', 'name_eng',
  'name_short', 'name', 'new_status', 'order', 'order_date', 'os_auc', 'os', 'packing', 'pay_date_executed',
  'pay_date_expected', 'phone', 'po_approval_date', 'po_delivery_date', 'po_status', 'po_summ', 'po_sum',
  'po', 'price', 'received_amount', 'receiving_date_actual', 'receiving_date_planned', 'referral_letter',
  'referral_letter_date', 'registration_at', 'registration_certificates', 'serie', 'shared_order',
  'shipment_id', 'spec_date',
  'spec_serial', 'spec', 'status', 'stock', 'storage_conditions', 'total_price', 'unit_price', 'rate_nds',
  'total_price_nds', 'unit_price_nds', 'unit', 'valid_till', 'value', 'vendor', 'key', 'vortex_go_id',
  'vortex_reg_id', 'vortex_loc_id', 'writeoffs', 'roles', 'notification_settings', 'item_packing',
  'decline_comment', 'approval_text', 'purchase', 'tax_total_w_medicines', 'tax_price_total_w_medicines',
  'price_total_w_medicines', 'available_amount', 'reserved_amount', 'reserved_amount_w_medicines',
  'available_amount_w_medicines', 'tax_total_w_materials_nd', 'tax_price_total_w_materials_nd',
  'price_total_w_materials_nd', 'reserved_amount_w_materials_nd', 'available_amount_w_materials_nd',
  'tax_total_w_materials_comm', 'tax_price_total_w_materials_comm', 'price_total_w_materials_comm',
  'reserved_amount_w_materials_comm', 'available_amount_w_materials_comm', 'tax_total_w_devices',
  'tax_price_total_w_devices', 'price_total_w_devices', 'reserved_amount_w_devices',
  'available_amount_w_devices', 'tax_total_w_consumables', 'tax_price_total_w_consumables',
  'price_total_w_consumables', 'reserved_amount_w_consumables', 'available_amount_w_consumables',
  'deleted', 'status_text', 'archive',
]

const fieldLabelModels = {
  Activities: { shipment_date_scheduled: true, created_by: true, responsible_manager: true },
  ActivityTypes: { name: true, parent_type: true },
  Consumables: { code: true, description: true },
  Devices: {
    code: true,
    diameter: true,
    length: true,
    particle_size: true,
    pore_size: true,
    sorbent: true,
    description: true,
  },
  MaterialsNd: { code: true, description: true },
  MaterialsComm: { description: true },
  Medicines: {
    product_form: true,
    registration_certificate: true,
    registration_certificates: true,
    description: true,
    ru_reissue_date: true,
    ru_owner: true,
  },
  MedicineManufacturer: { prod_stage: true },
  MedicineTrademarks: { code: true, medicinal_form: true },
  ConsumablesExpenses: { order: true },
  DevicesExpenses: { order: true },
  MaterialsCommExpenses: { order: true },
  MaterialsNdExpenses: { order: true },
  MedicinesExpenses: { order: true },
  Orders: {
    linked_order: true,
    linked_orders: true,
    testing_price: true,
    testing_tax_price: true,
    dispatch_date_executed: true,
    test_date_expected: true,
    test_date_executed: true,
    date_closed: true,
  },
  Uploads: {
    metadata: {
      lab_q_num: true,
      lab_q_date: true,
      num_act: true,
      date_act: true,
      num_test: true,
      date_test: true,
      num_wo: true,
      date_wo: true,
      num_l_order: true,
      date_l_order: true,
      invoice_no: true,
      invoice_date: true,
      name: true,
      num: true,
      date: true,
      spec_num: true,
      spec_date: true,
    },
  },
  User: {
    email_notifications: true,
    notifications_level: true,
    data_actual: true,
    account_updater_id: true,
    active: true,
    fio: true,
    username: true,
    email: true,
  },
  Laboratories: { name: true },
  Receptions: { name: true, date_acceptanced: true },
  Vendors: { name: true },
  Manufacturers: { name: true, address: true },
  ActionRequests: {
    approved: true,
    comment_approve: fmtCommentApprove,
  },
  Ticket: {
    title: true,
    description: true,
    module: true,
    tracker: true,
    priority: true,
    status: true,
    author: true,
    assigned_to: true,
    date_created: true,
    date_updated: true,
    date_in_progress: true,
    date_closed: true,
  },
}

function resolveFieldLabel(modelName, path) {
  const flat = Array.isArray(path) ? path.join('.') : String(path)
  return historyFieldLabel(modelName, flat, flat)
}

function createNestedFieldProxy(modelName, spec, prefix = '') {
  return new Proxy(spec, {
    get(target, prop) {
      if (typeof prop !== 'string') return undefined
      const val = target[prop]
      if (typeof val === 'function') return val
      if (val && typeof val === 'object') {
        const nextPrefix = prefix ? `${prefix}.${prop}` : prop
        return createNestedFieldProxy(modelName, val, nextPrefix)
      }
      const path = prefix ? `${prefix}.${prop}` : prop
      return resolveFieldLabel(modelName, path)
    },
    has(target, prop) {
      return typeof prop === 'string' && prop in target
    },
    ownKeys(target) {
      return Reflect.ownKeys(target)
    },
    getOwnPropertyDescriptor(target, prop) {
      if (!(prop in target)) return undefined
      return { enumerable: true, configurable: true }
    },
  })
}

function fmtCommentApprove(change, rel) {
  const value = change.value ?? change.old_value ?? ''
  const safe = DOMPurify.sanitize(`<span class="text-danger d-inline-block">${String(value)}</span>`)
  const model = rel[change.model_name]
  if (model) {
    const approved = model.find(d => d.id === change.model_id)?.approved ?? false
    if (approved) {
      return `<span class="text-muted">${historyPhrase('parts.approvedWithComment')}</span> ${safe}`
    }
  }
  return `<span class="text-muted">${historyPhrase('parts.declinedWithComment')}</span> ${safe}`
}

// if function, here then no composition
// the function must return a string as hist entry
const modelFieldFormatters = Object.fromEntries(
  Object.entries(models).flatMap(([name, m]) => (m.fields ? [[name, m.fields]] : []))
)

const fieldsFormatters = {
  assign_user: fmtAssignedUser,
  ...modelFieldFormatters,
  Comments(ch) {
    const comment = JSON.parse(ch.value)
    return `<span class="text-muted">${historyPhrase('actions.commentAddedInline')}</span> ${comment.text}`
  },
}

export const fields = new Proxy(fieldsFormatters, {
  get(target, prop) {
    if (typeof prop !== 'string') return undefined
    if (prop in target) return target[prop]
    if (prop in fieldLabelModels) {
      const spec = fieldLabelModels[prop]
      if (typeof spec === 'object') return createNestedFieldProxy(prop, spec)
    }
    if (FIELD_COMMON_KEYS.includes(prop)) {
      return resolveFieldLabel('', prop)
    }
    return undefined
  },
  has(_, prop) {
    return (
      typeof prop === 'string' &&
      (prop in fieldsFormatters || prop in fieldLabelModels || FIELD_COMMON_KEYS.includes(prop))
    )
  },
  ownKeys() {
    return [...new Set([...FIELD_COMMON_KEYS, ...Object.keys(fieldLabelModels), ...Object.keys(fieldsFormatters)])]
  },
  getOwnPropertyDescriptor(_, prop) {
    if (typeof prop !== 'string') return undefined
    if (!(prop in fieldsFormatters || prop in fieldLabelModels || FIELD_COMMON_KEYS.includes(prop))) {
      return undefined
    }
    return { enumerable: true, configurable: true }
  },
})

export const values = () => ({
  activity: (_, change, __, prop) =>
    change.display || (change[prop] ? `Активность №${change[prop]}` : change[prop]),
  activity_subtype: valueRelation,
  activity_type: valueRelation,
  /**
   * @type {ChangeValueGetter} aligned
   */
  aligned: (batch, change, __, prop) => {
    const firstTimeAligned =
        (batch.changes.find(c => c.path.includes('aligned_by')) ?? {}).old_value === null &&
        prop == 'old_value'
    let val = change[prop]
    switch (change[prop]) {
      case 'True':
      case true:
        val = historyPhrase('values.aligned')
        break
      case 'False':
      case false:
        val = firstTimeAligned ? null : historyPhrase('values.declined')
        break
    }
    return val
  },
  aligned_by: valueAssignUser,
  assign_role: function (_, change, rel, prop) {
    if (isObject(rel)) {
      if (rel['Role'] && Array.isArray(rel['Role'])) {
        const role = rel['Role'].find(d => d.id === Number(change[prop]))
        return role?.caption || ''
      }
    }
    return valueRelation(...arguments)
  },
  assign_user: valueAssignUser,
  responsible_manager: valueAssignUser,
  archived_at: valueDate,
  bill_date: valueDate,
  consignment_date: valueDate,
  consumable: valueRelation,
  date_created: valueDate,
  date_from: valueDate,
  date_to: valueDate,
  date_aligned: valueDate,
  date_updated: valueDate,
  date_in_progress: valueDate,
  date_closed: valueDate,
  assigned_to: valueAssignUser,
  author: valueAssignUser,
  destruction: function (_, change, rel, prop) {
    if (isObject(rel) && Array.isArray(rel.Destructions)) {
      const destruction = rel.Destructions.find(d => d.id === Number(change[prop]))
      if (destruction?.id != null) return `№${destruction.id}`
    }
    return change.display || (change[prop] ? `№${change[prop]}` : change[prop])
  },
  device: valueRelation,
  dispatch_date_expected: valueDate,
  gtd_date: valueDate,
  laboratory: function (_, change, rel, prop) {
    if (isObject(rel)) {
      if (rel['Laboratories'] && Array.isArray(rel['Laboratories'])) {
        const laboratory = rel['Laboratories'].find(d => d.id === Number(change[prop]))
        return laboratory?.name || ''
      }
    }
    return valueRelation(...arguments)
  },
  material_comm: valueRelation,
  material_nd: valueRelation,
  medicine: function (ch, change, rel, prop) {
    if (!change.relation) change.relation = 'Medicines'
    if (Array.isArray(rel?.Medicines)) {
      const id = Number(change[prop])
      const m = rel.Medicines.find(
        d => d?.id === id || String(d?.id) === String(change[prop])
      )
      const name = namedEntityLabel(m) || namedEntityLabel(m?.medicine_trademark)
      if (name) return name
    }
    const fromRel = valueRelation(ch, change, rel, prop)
    if (fromRel != null && fromRel !== '' && String(fromRel) !== String(change[prop])) {
      return fromRel
    }
    return change.display || change[prop]
  },
  medicine_trademark: valueMedicineTrademark,
  order_date: valueDate,
  parent_type: valueRelation,
  pay_date_executed: valueDate,
  pay_date_expected: valueDate,
  po_approval_date: valueDate,
  po_delivery_date: valueDate,
  airbill_date: valueDate,
  invoice_date: valueDate,
  import_permit_date: valueDate,
  referral_letter_date: valueDate,
  receiving_date_actual: valueDate,
  receiving_date_planned: valueDate,
  registration_at: valueDate,
  registration_certificates: valueRegistrationCertificates,
  spec_date: valueDate,
  ru_reissue_date: valueDate,
  date_acceptanced: valueDate,
  roles: valueRoleHistory,
  notification_settings: valueNotificationSetting,
  export: valueBool,
  /**
   * @type {ChangeValueGetter} stock
   */
  stock: function (_, change, rel, prop) {
    if (isObject(rel)) {
      const stockRelation =
        typeof change.model_name === 'string' && change.model_name.endsWith('Expenses')
          ? change.model_name.replace('Expenses', 'Stock')
          : null
      if (stockRelation && rel[stockRelation]) {
        const stockId = change[prop]
        const sid = Number(stockId)
        const list = rel[stockRelation]
        const stock =
          Array.isArray(list)
            ? list.find(d => d.id === sid)
            : list[sid] ?? list[String(stockId)] ?? list[sid]
        const serie = stock?.serie
        if (serie) {
          return historyPhrase('values.serie', { value: serie })
        }
      }
      const v = valueRelation(...arguments)
      return v != null && v !== '' ? historyPhrase('values.serie', { value: v }) : change[prop]
    }
    const v = valueRelation(...arguments)
    return v != null && v !== '' ? historyPhrase('values.serie', { value: v }) : change[prop]
  },
  valid_till: valueDate,
  vendor: valueRelation,
  order: function (_, change, rel, prop) {
    if (change.display) return change.display
    const raw = change[prop]
    if (isObject(rel) && Array.isArray(rel.Orders)) {
      const order = rel.Orders.find(
        d => d.id === Number(raw) || d.order_id === Number(raw) || String(d.id) === String(raw)
      )
      if (order?.order_id != null && order.order_id !== '') {
        return `№${order.order_id}`
      }
    }
    return raw != null && raw !== '' ? `№${raw}` : raw
  },
  shared_order: function (_, change, rel, prop) {
    if (isObject(rel) && Array.isArray(rel['Orders'])) {
      const order = rel['Orders'].find(d => d.id === Number(change[prop]))
      if (order && order.order_id != null && order.order_id !== '') {
        return historyPhrase('values.reserve', { value: order.order_id })
      }
      return ''
    }
    const v = valueRelation(...arguments)
    return v != null && v !== '' ? historyPhrase('values.reserve', { value: v }) : ''
  },
  //
  Activities: {
    shipment_date_scheduled: valueDate,
    created_by: valueAssignUser,
    responsible_manager: valueAssignUser,
  },
  ActionRequests: {
    approved: valueBool,
  },
  Consumables: {
    packing: valueConfigId('consumables.packing'),
    storage_conditions: valueConfigId('consumables.storage_conditions'),
    archive: valueBool,
  },
  ConsumablesExpenses: {
    status: valueConfigId('consumables_expenses.status'),
  },
  ConsumablesPurchases: {
    status: valueConfigId('consumables_purchases.status'),
  },
  ConsumablesStock: {
    status: valueConfigId('consumables_stock.status'),
    item_packing: valueConfigId('consumables_stock.item_packing'),
  },
  //
  Devices: {
    packing: valueConfigId('devices.packing'),
    storage_conditions: valueConfigId('devices.storage_conditions'),
    archive: valueBool,
  },
  DevicesExpenses: {
    status: valueConfigId('devices_expenses.status'),
  },
  DevicesPurchases: {
    status: valueConfigId('devices_purchases.status'),
  },
  DevicesStock: {
    status: valueConfigId('devices_stock.status'),
    item_packing: valueConfigId('devices_stock.item_packing'),
  },
  //
  MaterialsComm: {
    packing: valueConfigId('materials_comm.packing'),
    storage_conditions: valueConfigId('materials_comm.storage_conditions'),
    archive: valueBool,
  },
  MaterialsCommExpenses: {
    status: valueConfigId('materials_comm_expenses.status'),
  },
  MaterialsCommPurchases: {
    status: valueConfigId('materials_comm_purchases.status'),
  },
  MaterialsCommStock: {
    status: valueConfigId('materials_comm_stock.status'),
    item_packing: valueConfigId('materials_comm_stock.item_packing'),
  },
  //
  MaterialsNd: {
    packing: valueConfigId('materials_nd.packing'),
    storage_conditions: valueConfigId('materials_nd.storage_conditions'),
    archive: valueBool,
  },
  MaterialsNdExpenses: {
    status: valueConfigId('materials_nd_expenses.status'),
  },
  MaterialsNdPurchases: {
    status: valueConfigId('materials_nd_purchases.status'),
  },
  MaterialsNdStock: {
    status: valueConfigId('materials_nd_stock.status'),
    item_packing: valueConfigId('materials_nd_stock.item_packing'),
  },
  //
  Medicines: {
    packing: valueConfigId('medicines.packing'),
    product_form: valueConfigId('medicines.product_form'),
    storage_conditions: valueConfigId('medicines.storage_conditions'),
    registration_at: valueDate,
    registration_certificates: valueRegistrationCertificates,
  },
  MedicineManufacturer: {
    prod_stage: valueConfigId('medicines.prod_stage'),
  },
  MedicinesExpenses: {
    status: valueConfigId('medicines_expenses.status'),
  },
  MedicinesPurchases: {
    status: valueConfigId('medicines_purchases.status'),
  },
  MedicinesStock: {
    status: valueConfigId('medicines_stock.status'),
    item_packing: valueConfigId('medicines_stock.item_packing'),
  },
  MedicineTrademarks: {
    medicinal_form: valueConfigId('medicine_trademarks.medicinal_form'),
  },
  Ticket: {
    status: (_, change, __, prop) => ticketEnumValue('statuses', change, prop),
    priority: (_, change, __, prop) => ticketEnumValue('priorities', change, prop),
    tracker: (_, change, __, prop) => ticketEnumValue('trackers', change, prop),
    module: (_, change, __, prop) => {
      const v = change[prop]
      return v != null && v !== '' ? String(v).toUpperCase() : v
    },
    author: valueAssignUser,
    assigned_to: valueAssignUser,
    date_created: valueDate,
    date_updated: valueDate,
    date_in_progress: valueDate,
    date_closed: valueDate,
  },
  //
  Orders: {
    status: valueConfigId('orders.status'),
    dispatch_date_executed: valueDate,
    test_date_expected: valueDate,
    test_date_executed: valueDate,
    date_closed: valueDate,
  },
  //
  Destructions: {
    status: valueConfigId('destructions.status'),
  },
  //
  ConsumablesDestructions: {
    status: valueConfigId('consumables_destructions.status'),
  },
  DevicesDestructions: {
    status: valueConfigId('devices_destructions.status'),
  },
  MaterialsCommDestructions: {
    status: valueConfigId('materials_comm_destructions.status'),
  },
  MaterialsNdDestructions: {
    status: valueConfigId('materials_nd_destructions.status'),
  },
  MedicinesDestructions: {
    status: valueConfigId('medicines_destructions.status'),
  },
  AssignedUsers: {
    assigned_user: valueAssignUser,
  },
  User: {
    roles: valueRoleHistory,
    notification_settings: valueNotificationSetting,
    notifications_level: function (_, change, __, prop) {
      const levels = {
        0: historyPhrase('notificationsLevel.none'),
        1: historyPhrase('notificationsLevel.critical'),
        2: historyPhrase('notificationsLevel.criticalAndNormal'),
        3: historyPhrase('notificationsLevel.all'),
      }
      return levels[Number(change[prop])] || ''
    },
    data_actual: function (_, change, __, prop) {
      switch (change[prop]) {
        case 'True':
        case true:
        case 1:
        case '1':
          return historyPhrase('values.dataActual')
        case 'False':
        case false:
        case 0:
        case '0':
          return historyPhrase('values.dataOutdated')
        default:
          return change[prop] ?? ''
      }
    },
    active: function (_, change, __, prop) {
      switch (change[prop]) {
        case 'True':
        case true:
        case 1:
        case '1':
          return historyPhrase('values.activated')
        case 'False':
        case false:
        case 0:
        case '0':
          return historyPhrase('values.deactivated')
        default:
          return change[prop] ?? ''
      }
    },
    account_updater_id: valueAssignUser,
  },
  Uploads: {
    metadata: {
      lab_q_date: valueDate,
      date_act: valueDate,
      date_test: valueDate,
      date_wo: valueDate,
      date_l_order: valueDate,
      invoice_date: valueDate,
      date: valueDate,
    },
  },  
})

export function url(model, id) {
  const cfg = models[model]
  const prefix = cfg?.historyPrefix || cfg?.prefix
  if (!prefix) {
    return null
  }
  return `${prefix}/${id}/history`
}

export function getVal(ch, change, rel, prop = 'value') {
  const v = values()
  let res = _get(v, [ch.model_name, ...change.path]) || _get(v, change.path)
  if (!res) {
    res = () => change[prop]
  }
  let val = res(ch, change, rel, prop)
  if (isRef(val) || isReactive(val)) {
    val = toValue(val)
  }
  return val ?? ''
}

// --
/**
 * @type {ChangeValueGetter} valueAssignUser
 */
function valueAssignUser(_, change, rel, prop) {
  let u = _result(rel, [change.relation, change[prop]])
  if (Array.isArray(rel['User'])) {
    u = rel['User'].find(d => d.id === Number(change[prop]))
  }
  return u?.fio || u?.username
}
/**
 * @type {ChangeValueGetter} valueRelation
 */
function valueRelation(_, change, rel, prop) {
  const raw = _result(rel, [change.relation, change[prop]], change[prop])
  let v = raw
  if (typeof raw === 'string') {
    const t = raw.trim()
    if (t.startsWith('{') && t.endsWith('}')) {
      try {
        const p = JSON.parse(t)
        if (p && typeof p === 'object' && !Array.isArray(p)) v = p
      } catch {
        /* оставляем строку */
      }
    }
  }
  if (isObject(v) && !Array.isArray(v)) {
    // Связь на User (SSO отдаёт {username, fio})
    const username = (v.username ?? '').toString().trim()
    const fio = (v.fio ?? '').toString().trim()
    if (username || fio) {
      return [username, fio].filter(Boolean).join(', ')
    }
    const notifLabel = formatNotificationSettingHistoryLabel(v)
    if (notifLabel && (v.caption != null || v.service != null || v.name != null)) {
      return notifLabel
    }
    const caption = v.caption || namedEntityLabel(v) || v.value || ''
    return caption || (raw != null && typeof raw !== 'object' ? raw : '')
  }
  return raw
}

const HISTORY_NOTIFICATION_SERVICE_LABELS = {
  qems: 'QEMS',
  qdms: 'QDMS',
  rtms: 'RTMS',
  ltms: 'LTMS',
  catalogs: 'Catalogs',
  sso: 'SSO',
}

/** Подпись NotificationSettings для истории User (как в QEMS). */
function formatNotificationSettingHistoryLabel(raw) {
  if (raw == null || raw === '') return ''
  let v = raw
  if (typeof v === 'string') {
    const t = v.trim()
    if (t.startsWith('{') && t.endsWith('}')) {
      try {
        const p = JSON.parse(t)
        if (p && typeof p === 'object' && !Array.isArray(p)) v = p
      } catch {
        /* оставляем строку */
      }
    }
  }
  if (isObject(v) && !Array.isArray(v)) {
    const caption = String(v.caption ?? v.name ?? v.value ?? '').trim()
    const serviceKey = String(v.service ?? '').trim().toLowerCase()
    const serviceLabel =
      HISTORY_NOTIFICATION_SERVICE_LABELS[serviceKey] ||
      (serviceKey ? serviceKey.toUpperCase() : '')
    if (caption && serviceLabel) {
      return historyPhrase('values.module', { caption, service: serviceLabel })
    }
    if (caption) return caption
  }
  const s = String(v).trim()
  return s && s !== 'undefined' && s !== 'null' && s !== '[object Object]' ? s : ''
}

/** Подпись Role для истории User. */
function formatRoleHistoryLabel(raw, rel) {
  if (raw == null || raw === '') return ''
  let id = raw
  let direct = null
  if (isObject(raw) && !Array.isArray(raw)) {
    direct = raw
    if (raw.id != null && raw.id !== '') id = raw.id
  }
  const fromRel =
    _result(rel, ['Role', id]) ??
    _result(rel, ['Role', String(id)]) ??
    _result(rel, ['Role', Number(id)])
  const candidate = fromRel != null && fromRel !== '' ? fromRel : direct
  if (isObject(candidate) && !Array.isArray(candidate)) {
    return String(candidate.caption ?? candidate.name ?? '').trim() || String(id)
  }
  if (typeof candidate === 'string' && candidate.trim()) {
    return candidate.trim()
  }
  const s = String(id).trim()
  return s && s !== '[object Object]' ? s : ''
}

/** @type {ChangeValueGetter} */
function valueRoleHistory(_, change, rel, prop) {
  return formatRoleHistoryLabel(change[prop], rel)
}

/** @type {ChangeValueGetter} */
function valueNotificationSetting(_, change, rel, prop) {
  const raw = change[prop]
  const fromRelated =
    _result(rel, [change.relation || 'NotificationSettings', raw]) ??
    _result(rel, ['NotificationSettings', raw]) ??
    _result(rel, ['NotificationSettings', String(raw)]) ??
    _result(rel, ['NotificationSettings', Number(raw)])
  return (
    formatNotificationSettingHistoryLabel(fromRelated) ||
    formatNotificationSettingHistoryLabel(raw) ||
    (raw != null && typeof raw !== 'object' ? String(raw) : '')
  )
}

/**
 * @type {ChangeValueGetter} valueMedicineTrademark
 */
function valueMedicineTrademark(_, change, rel, prop) {
  const raw = change[prop]
  const direct = _result(rel, [change.relation, raw])
  if (typeof direct === 'string') {
    return direct
  }
  if (isObject(direct)) {
    return namedEntityLabel(direct) || raw
  }

  const byModelName = rel?.MedicineTrademarks
  if (Array.isArray(byModelName)) {
    const tm = byModelName.find(d => Number(d.id) === Number(raw))
    if (tm) {
      return namedEntityLabel(tm) || raw
    }
  } else if (isObject(byModelName)) {
    const tm = byModelName[raw] || byModelName[String(raw)] || byModelName[Number(raw)]
    if (isObject(tm)) {
      return namedEntityLabel(tm) || raw
    }
    if (typeof tm === 'string') {
      return tm
    }
  }

  return raw
}
/**
 * @param {string} pth
 * @returns {ChangeValueGetter}
 */
function valueConfigId(pth) {
  const configStore = useConfigsStore()
  return (_, change, __, prop) => {
    const v = configStore.getById(pth, change[prop])
    return v
  }
}
/**
 * @type {ChangeValueGetter} valueDate
 */
function valueDate(_, change, __, prop) {
  const val = change[prop]
  if (!val || val === '0000-00-00') {
    return null
  }
  // AS-908: начальные значения из истории уже отформатированы как dd.MM.yyyy [HH:mm]
  if (/^\d{2}\.\d{2}\.\d{4}/.test(String(val))) {
    return val
  }
  if (isNaN(new Date(val).getTime())) {
    return null
  }
  return commons.dateLocal(val)
}

/**
 * @type {ChangeValueGetter} valueRegistrationCertificates
 */
function valueRegistrationCertificates(_, change, __, prop) {
  const raw = change[prop]
  if (raw == null || raw === '') return ''
  let list = raw
  if (typeof raw === 'string') {
    try {
      list = JSON.parse(raw)
    } catch {
      return raw
    }
  }
  if (!Array.isArray(list)) {
    if (list && typeof list === 'object') {
      list = [list]
    } else {
      return String(raw)
    }
  }
  return list
    .map(item => {
      if (!item || typeof item !== 'object') return String(item ?? '')
      const cert = item.certificate || item.registration_certificate || ''
      const dateRaw = item.date || item.registration_at
      const date = dateRaw ? commons.dateLocal(dateRaw) || String(dateRaw) : ''
      if (cert && date) return `${cert} (${date})`
      return cert || date || ''
    })
    .filter(Boolean)
    .join('; ')
}
// --
/**
 * @type {ChangeValueGetter} valueBool
 */
function valueBool(_, change, __, prop) {
  const raw = change[prop]
  const val = raw === true || raw === 'True' || raw === 'true'
  return historyFormatBool(val)
}
// --
/**
 * @type {ChangeFormatter} fmtAssignedUser
 */
function fmtAssignedUser(change, relations) {
  if (change.value === null) {
    return historyPhrase('values.assigneeRemoved')
  }
  let rel = _result(relations, [change.relation, change.value], {})
  if (Array.isArray(relations[change.relation])) {
    rel = relations[change.relation].find(d => d.id === Number(change.value))
  }
  const str = [
    historyPhrase('values.assigneeSet', { user: `<span class="text-success">${rel?.fio || rel?.username}</span>` }),
  ]
  return str.join(' ')
}

/**
 * Как fmtAssignedUser, но с указанием роли (для модели AssignedUsers).
 * Ожидает change.model_id для доступа к relations.AssignedUsers[model_id].
 * @type {ChangeFormatter} fmtAssignedUserWithRole
 */
function fmtAssignedUserWithRole(change, relations) {
  if (change.value === null) {
    return historyPhrase('values.assigneeRemoved')
  }
  let userRel = _result(relations, [change.relation, change.value], {})
  if (Array.isArray(relations[change.relation])) {
    userRel = relations[change.relation].find(d => d.id === Number(change.value))
  }
  const userName = userRel?.fio || userRel?.username || ''
  const assignedRec = change.model_id != null && relations.AssignedUsers?.[change.model_id]
  const roleName = assignedRec?.assigned_role?.name ?? assignedRec?.assigned_role_name ?? ''
  const parts = [historyPhrase('values.assigneeSet', { user: `<span class="text-success">${userName}</span>` })]
  if (roleName) {
    parts.push(historyPhrase('values.assigneeRole', { role: `<span class="text-success">${roleName}</span>` }))
  }
  return parts.join(' ')
}
